import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import ledgerViewsApi from '../../../api/ledgerViewsApi';
import vendorApi from '../../../api/vendorApi';
import { LedgerDatasetFilterParams, QboAccount, StandardAccount } from '../../../api/types';

interface UseAccountingQueriesParams {
    deepLinkQboBillId: string | null;
    selectedBusinessId?: string;
    isGlCodeView: boolean;
    datasetFilters: Omit<LedgerDatasetFilterParams, 'source'>;
    glCodeQboType: string;
    glCodeClassification: string;
    glCodeFinzCategory: string;
}

const useAccountingQueries = ({
    deepLinkQboBillId,
    selectedBusinessId,
    isGlCodeView,
    datasetFilters,
    glCodeQboType,
    glCodeClassification,
    glCodeFinzCategory,
}: UseAccountingQueriesParams) => {
    const queryClient = useQueryClient();

    const { data: deepLinkedBill } = useQuery({
        queryKey: ['ledger-qbo-bill', deepLinkQboBillId, selectedBusinessId],
        queryFn: () => ledgerViewsApi.getQboBillById(deepLinkQboBillId || '', selectedBusinessId || undefined),
        enabled: !!deepLinkQboBillId && !!selectedBusinessId,
    });

    const { data: glCodeQboData, isLoading: isGlCodeQboLoading } = useQuery({
        queryKey: ['qboAccounts', selectedBusinessId],
        queryFn: () => vendorApi.getQBOAccounts(selectedBusinessId || undefined),
        enabled: !!selectedBusinessId && isGlCodeView,
    });

    const { data: glCodeStandardAccountsData, isLoading: isGlCodeStandardAccountsLoading } = useQuery({
        queryKey: ['standardAccounts', selectedBusinessId],
        queryFn: () => vendorApi.getStandardAccounts(selectedBusinessId || undefined),
        enabled: !!selectedBusinessId && isGlCodeView,
    });

    const updateGlCodeMappingMutation = useMutation({
        mutationFn: ({ qboAccountId, standardAccountId }: { qboAccountId: string; standardAccountId: string | null }) =>
            vendorApi.updateQboAccountMapping(
                qboAccountId,
                {
                    standard_account_id: standardAccountId,
                    apply_to_transactions: true,
                },
                selectedBusinessId || undefined,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['qboAccounts', selectedBusinessId] });
            queryClient.invalidateQueries({ queryKey: ['standardAccounts', selectedBusinessId] });
        },
    });

    const syncAccountingQboMutation = useMutation({
        mutationFn: async (businessId: string) => ledgerViewsApi.refreshAccounting(businessId),
        onSuccess: async (_, businessId) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['ledger-dataset'] }),
                queryClient.invalidateQueries({ queryKey: ['ledger-filter-options'] }),
                queryClient.invalidateQueries({ queryKey: ['ledger-qbo-bill'] }),
                queryClient.invalidateQueries({ queryKey: ['ledger-vendor-directory'] }),
                queryClient.invalidateQueries({ queryKey: ['qboAccounts', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['cash-qbo-accounts', businessId] }),
                queryClient.invalidateQueries({ queryKey: ['standardAccounts', businessId] }),
            ]);
        },
    });

    const glCodeQboAccounts = useMemo(
        () => Object.values(glCodeQboData?.groups || {}).flat() as QboAccount[],
        [glCodeQboData],
    );
    const glCodeStandardAccounts = useMemo(
        () => (glCodeStandardAccountsData?.items || []) as StandardAccount[],
        [glCodeStandardAccountsData],
    );
    const glCodeQboTypeOptions = useMemo(
        () => Array.from(new Set(glCodeQboAccounts.map((account) => account.data.account_type).filter(Boolean)))
            .sort((left, right) => String(left).localeCompare(String(right))),
        [glCodeQboAccounts],
    );
    const glCodeClassificationOptions = useMemo(
        () => Array.from(new Set(glCodeQboAccounts.map((account) => account.data.classification).filter(Boolean)))
            .sort((left, right) => String(left).localeCompare(String(right))),
        [glCodeQboAccounts],
    );
    const glCodeFinzCategoryOptions = useMemo(
        () => glCodeStandardAccounts
            .map((account) => account.fiscal_bucket)
            .filter((value): value is string => Boolean(value))
            .filter((value, index, values) => values.indexOf(value) === index)
            .sort((left, right) => left.localeCompare(right)),
        [glCodeStandardAccounts],
    );
    const paginatedGlCodeAccounts = useMemo(() => {
        const filtered = glCodeQboAccounts.filter((account) => {
            const searchValue = (datasetFilters.search || '').trim().toLowerCase();
            const groupLabel = account.data.account_type || 'Unknown';
            if (glCodeQboType && (account.data.account_type || '') !== glCodeQboType) return false;
            if (glCodeClassification && (account.data.classification || '') !== glCodeClassification) return false;
            if (glCodeFinzCategory && (account.mapping?.fiscal_bucket || '') !== glCodeFinzCategory) return false;
            if (searchValue) {
                const haystacks = [
                    account.data.fully_qualified_name || '',
                    account.data.name || '',
                    account.data.account_id || '',
                    account.data.account_type || '',
                    account.data.sub_type || '',
                    account.data.classification || '',
                    groupLabel,
                    account.mapping?.finz_account_name || '',
                    account.mapping?.fiscal_bucket || '',
                    account.mapping?.finz_account_number || '',
                ].map((value) => value.toLowerCase());
                if (!haystacks.some((value) => value.includes(searchValue))) return false;
            }
            return true;
        });
        const start = ((datasetFilters.page || 1) - 1) * (datasetFilters.page_size || 50);
        const end = start + (datasetFilters.page_size || 50);
        return {
            total: filtered.length,
            items: filtered.slice(start, end),
        };
    }, [datasetFilters.page, datasetFilters.page_size, datasetFilters.search, glCodeClassification, glCodeFinzCategory, glCodeQboAccounts, glCodeQboType]);

    return {
        deepLinkedBill,
        glCodeQboData,
        isGlCodeQboLoading,
        glCodeStandardAccountsData,
        isGlCodeStandardAccountsLoading,
        updateGlCodeMappingMutation,
        syncAccountingQboMutation,
        glCodeQboAccounts,
        glCodeStandardAccounts,
        glCodeQboTypeOptions,
        glCodeClassificationOptions,
        glCodeFinzCategoryOptions,
        paginatedGlCodeAccounts,
    };
};

export default useAccountingQueries;
